/**
 * Browser-side web-push enrollment for the dashboard PWA.
 *
 * Flow: register /sw.js → Notification.requestPermission() →
 * pushManager.subscribe(VAPID public key) → POST /api/push/subscribe.
 *
 * iOS requires the PWA to be installed to the home screen (16.4+) before
 * push is available — pushSupportState() distinguishes that case so the UI
 * can say so instead of failing opaquely.
 */

import { api } from "./api.js";

export type PushSupport =
  | "supported"
  | "needs-install" // iOS Safari tab — must be added to the home screen first
  | "unsupported";

export function pushSupportState(): PushSupport {
  if ("serviceWorker" in navigator && "PushManager" in window && "Notification" in window) {
    return "supported";
  }
  // iOS Safari before home-screen install exposes none of the push API.
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  if (isIos && !standalone) return "needs-install";
  return "unsupported";
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupportState() !== "supported") return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

function b64uToUint8(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function keyToB64u(sub: PushSubscription, name: "p256dh" | "auth"): string {
  const buf = sub.getKey(name);
  if (!buf) throw new Error(`subscription missing ${name} key`);
  let bin = "";
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Subscribe this device and register it with the API. Throws with a readable message. */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("notification permission was not granted");
  }
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64uToUint8(vapidPublicKey) as unknown as BufferSource,
    }));

  await api.post("/api/push/subscribe", {
    endpoint: sub.endpoint,
    p256dh: keyToB64u(sub, "p256dh"),
    auth: keyToB64u(sub, "auth"),
    label: navigator.userAgent.includes("iPhone") ? "iPhone home screen" : navigator.platform || "browser",
  });
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.post("/api/push/unsubscribe", { endpoint: sub.endpoint });
  await sub.unsubscribe();
}
